/**
 * TokenDiscoveryPopup Component
 *
 * Shows contextual slide-in notifications when users add specific tokens
 * to their portfolio, promoting related features:
 * - HBAR → Hedera Monitor
 * - FLUX → FluxNode Monitor
 * - CRO → Cronos Staking & Masternode
 *
 * Auto-enables the corresponding modules when notification is shown.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { usePageModules } from '../../hooks/usePageModules';

interface TokenDiscoveryPopupProps {
  /** List of token symbols in the portfolio */
  portfolioTokens: string[];
  /** Callback when user clicks to navigate to a page */
  onNavigate: (pageId: string) => void;
}

// Token to feature mapping
interface TokenFeature {
  token: string;
  tokenName: string;
  moduleId: string;
  pageId: string;
  icon: React.ComponentType<{ size?: number; color?: string }>;
  color: string;
  gradient: string;
  borderColor: string;
}

const TOKEN_FEATURES: TokenFeature[] = [
  {
    token: 'HBAR',
    tokenName: 'Hedera (HBAR)',
    moduleId: 'hedera-monitor',
    pageId: 'hedera-monitor',
    icon: Icons.Hash,
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(99, 102, 241, 0.15))',
    borderColor: 'rgba(139, 92, 246, 0.4)',
  },
  {
    token: 'FLUX',
    tokenName: 'Flux (FLUX)',
    moduleId: 'fluxnode-monitor',
    pageId: 'fluxnode-monitor',
    icon: Icons.Server,
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(99, 102, 241, 0.15))',
    borderColor: 'rgba(59, 130, 246, 0.4)',
  },
  {
    token: 'CRO',
    tokenName: 'Cronos (CRO)',
    moduleId: 'cronos-monitor',
    pageId: 'cronos-monitor',
    icon: Icons.CryptoCom,
    color: '#10b981',
    gradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(6, 182, 212, 0.15))',
    borderColor: 'rgba(16, 185, 129, 0.4)',
  },
];

const STORAGE_KEY_PREFIX = 'token_discovery_dismissed_';
const DISMISS_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days

export function TokenDiscoveryPopup({ portfolioTokens, onNavigate }: TokenDiscoveryPopupProps) {
  const { t } = useTranslation();
  const { enableModule, isModuleEnabled } = usePageModules();
  const [visiblePopup, setVisiblePopup] = useState<TokenFeature | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [shownTokens, setShownTokens] = useState<Set<string>>(new Set());

  // Use refs to track pending popup and avoid timer cancellation on re-renders
  const pendingTokenRef = useRef<string | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timer on unmount only
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // Check which tokens are in portfolio and haven't been dismissed
  useEffect(() => {
    // Filter out undefined/null tokens before processing
    const upperTokens = portfolioTokens
      .filter((t): t is string => t != null && typeof t === 'string')
      .map(t => t.toUpperCase());

    // Find the first token that should trigger a popup
    let featureToShow: TokenFeature | null = null;

    for (const feature of TOKEN_FEATURES) {
      // Skip if already shown in this session
      if (shownTokens.has(feature.token)) continue;

      // Skip if token not in portfolio
      if (!upperTokens.includes(feature.token)) continue;

      // Check if dismissed recently
      const dismissedAt = localStorage.getItem(`${STORAGE_KEY_PREFIX}${feature.token}`);
      if (dismissedAt) {
        const dismissedTime = parseInt(dismissedAt, 10);
        if (Date.now() - dismissedTime < DISMISS_DURATION) {
          continue;
        }
      }

      featureToShow = feature;
      break;
    }

    // If we found a token to show and no timer is already running for it
    if (featureToShow && pendingTokenRef.current !== featureToShow.token && !timerRef.current) {
      pendingTokenRef.current = featureToShow.token;
      const feature = featureToShow;

      // Show popup after a short delay
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        pendingTokenRef.current = null;
        setVisiblePopup(feature);
        setShownTokens(prev => new Set(prev).add(feature.token));

        // Auto-enable the module if not already enabled
        if (!isModuleEnabled(feature.moduleId)) {
          enableModule(feature.moduleId);
        }
      }, 2000);
    }

    // No cleanup - we want the timer to persist across re-renders
    // Timer will self-clean when it fires
  }, [portfolioTokens, shownTokens, isModuleEnabled, enableModule]);

  const handleClose = useCallback(() => {
    if (!visiblePopup) return;

    setIsClosing(true);
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${visiblePopup.token}`, Date.now().toString());

    setTimeout(() => {
      setIsClosing(false);
      setVisiblePopup(null);
    }, 300);
  }, [visiblePopup]);

  const handleNavigate = useCallback(() => {
    if (!visiblePopup) return;

    // Close the popup first
    setIsClosing(true);
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${visiblePopup.token}`, Date.now().toString());

    setTimeout(() => {
      setIsClosing(false);
      onNavigate(visiblePopup.pageId);
      setVisiblePopup(null);
    }, 300);
  }, [visiblePopup, onNavigate]);

  if (!visiblePopup) return null;

  const Icon = visiblePopup.icon;

  // Get translations based on token
  const getContent = () => {
    switch (visiblePopup.token) {
      case 'HBAR':
        return {
          title: t('tokenDiscovery.hbar.title', 'Hedera Monitor'),
          subtitle: t('tokenDiscovery.hbar.subtitle', 'Suivez votre staking HBAR'),
          description: t('tokenDiscovery.hbar.description', 'Vous avez ajouté HBAR à votre portfolio ! Découvrez la page Hedera Monitor pour suivre votre staking, vos rewards et l\'évolution de votre portefeuille Hedera.'),
          features: [
            t('tokenDiscovery.hbar.feature1', 'Suivi du staking'),
            t('tokenDiscovery.hbar.feature2', 'Rewards en temps réel'),
            t('tokenDiscovery.hbar.feature3', 'Historique des transactions'),
          ],
          cta: t('tokenDiscovery.hbar.cta', 'Voir Hedera Monitor'),
        };
      case 'FLUX':
        return {
          title: t('tokenDiscovery.flux.title', 'FluxNode Monitor'),
          subtitle: t('tokenDiscovery.flux.subtitle', 'Gérez vos FluxNodes'),
          description: t('tokenDiscovery.flux.description', 'Vous avez ajouté FLUX à votre portfolio ! Découvrez la page FluxNode Monitor pour suivre vos nodes, applications déployées, et bénéficier de l\'hébergement et du déploiement automatique.'),
          features: [
            t('tokenDiscovery.flux.feature1', 'Monitoring des nodes'),
            t('tokenDiscovery.flux.feature2', 'Applications déployées'),
            t('tokenDiscovery.flux.feature3', 'Hébergement & Auto-deploy'),
          ],
          cta: t('tokenDiscovery.flux.cta', 'Voir FluxNode Monitor'),
        };
      case 'CRO':
        return {
          title: t('tokenDiscovery.cro.title', 'Cronos Staking'),
          subtitle: t('tokenDiscovery.cro.subtitle', 'Staking & Lotto Masternode'),
          description: t('tokenDiscovery.cro.description', 'Vous avez ajouté CRO à votre portfolio ! Découvrez la page Cronos Staking pour suivre votre staking, participer à notre loterie Masternode et gagner des récompenses supplémentaires.'),
          features: [
            t('tokenDiscovery.cro.feature1', 'Suivi du staking CRO'),
            t('tokenDiscovery.cro.feature2', 'Loterie Masternode'),
            t('tokenDiscovery.cro.feature3', 'Récompenses bonus'),
          ],
          cta: t('tokenDiscovery.cro.cta', 'Voir Cronos Staking'),
        };
      default:
        return null;
    }
  };

  const content = getContent();
  if (!content) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        width: '340px',
        background: 'linear-gradient(135deg, rgba(30, 30, 40, 0.98), rgba(20, 20, 30, 0.98))',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(20px)',
        zIndex: 1000,
        overflow: 'hidden',
        animation: isClosing
          ? 'tokenDiscoverySlideOut 0.3s ease-in forwards'
          : 'tokenDiscoverySlideIn 0.4s ease-out',
      }}
    >
      {/* Gradient accent bar */}
      <div
        style={{
          height: '4px',
          background: `linear-gradient(90deg, ${visiblePopup.color}, ${visiblePopup.color}88, ${visiblePopup.color}44)`,
        }}
      />

      {/* Header */}
      <div style={{ padding: '16px 16px 12px', position: 'relative' }}>
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: 'none',
            borderRadius: '8px',
            padding: '6px',
            cursor: 'pointer',
            color: 'var(--muted)',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.color = 'var(--text)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.color = 'var(--muted)';
          }}
          aria-label={t('common.close')}
        >
          <Icons.X size={16} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '12px',
              background: visiblePopup.gradient,
              border: `1px solid ${visiblePopup.borderColor}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon size={24} color={visiblePopup.color} />
          </div>
          <div>
            <h4 style={{ fontSize: '15px', fontWeight: 700, margin: 0, color: 'var(--text)' }}>
              {content.title}
            </h4>
            <p style={{ fontSize: '11px', color: visiblePopup.color, margin: 0, fontWeight: 500 }}>
              {content.subtitle}
            </p>
          </div>
        </div>

        {/* Token badge */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 10px',
            background: visiblePopup.gradient,
            border: `1px solid ${visiblePopup.borderColor}`,
            borderRadius: '20px',
            fontSize: '11px',
            color: visiblePopup.color,
            fontWeight: 600,
            marginBottom: '8px',
          }}
        >
          <span style={{ fontSize: '14px' }}>✨</span>
          {t('tokenDiscovery.tokenDetected', '{{token}} détecté dans votre portfolio', { token: visiblePopup.tokenName })}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '0 16px 16px' }}>
        <p style={{
          fontSize: '12px',
          color: 'var(--muted)',
          margin: '0 0 14px',
          lineHeight: 1.5,
        }}>
          {content.description}
        </p>

        {/* Features */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
          {content.features.map((feature, index) => (
            <span
              key={index}
              style={{
                fontSize: '10px',
                padding: '5px 10px',
                background: 'rgba(16, 185, 129, 0.12)',
                color: '#10b981',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <Icons.Check size={10} />
              {feature}
            </span>
          ))}
        </div>

        {/* CTA Button */}
        <button
          onClick={handleNavigate}
          style={{
            width: '100%',
            padding: '12px 16px',
            background: `linear-gradient(135deg, ${visiblePopup.color}, ${visiblePopup.color}cc)`,
            border: 'none',
            borderRadius: '10px',
            color: 'white',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = `0 4px 20px ${visiblePopup.color}66`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {content.cta}
          <Icons.ArrowRight size={16} />
        </button>

        {/* Module auto-enabled hint */}
        <p style={{
          marginTop: '10px',
          fontSize: '10px',
          color: 'var(--muted)',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
        }}>
          <Icons.Check size={10} color="#10b981" />
          {t('tokenDiscovery.moduleEnabled', 'Module activé automatiquement dans la sidebar')}
        </p>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes tokenDiscoverySlideIn {
          from {
            transform: translateX(120%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes tokenDiscoverySlideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(120%);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
